import { useEffect, useRef, useState } from "react";
import { X, Mic, MicOff, Video, VideoOff, PhoneOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

interface VideoCallOverlayProps {
  roomUrl: string;
  partnerName: string;
  partnerAvatar?: string;
  onEnd: () => void;
  isIncoming?: boolean;
}

export const VideoCallOverlay = ({
  roomUrl,
  partnerName,
  partnerAvatar,
  onEnd,
  isIncoming = false,
}: VideoCallOverlayProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isConnecting, setIsConnecting] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);

  useEffect(() => {
    // Set connecting state to false after iframe loads
    const timer = setTimeout(() => {
      setIsConnecting(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleEndCall = () => {
    onEnd();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black flex flex-col"
      >
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={partnerAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${partnerName}`}
                alt={partnerName}
                className="w-10 h-10 rounded-full border-2 border-white/20"
              />
              <div>
                <p className="text-white font-medium">{partnerName}</p>
                <p className="text-white/60 text-sm">
                  {isConnecting ? "Connecting..." : "In call"}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              onClick={handleEndCall}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Video Container */}
        <div className="flex-1 relative">
          {isConnecting && (
            <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
              <div className="text-center">
                <img
                  src={partnerAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${partnerName}`}
                  alt={partnerName}
                  className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-primary animate-pulse"
                />
                <p className="text-white text-lg font-medium">{partnerName}</p>
                <p className="text-white/60 mt-2">
                  {isIncoming ? "Incoming call..." : "Connecting..."}
                </p>
              </div>
            </div>
          )}
          
          <iframe
            ref={iframeRef}
            src={roomUrl}
            allow="camera; microphone; fullscreen; speaker; display-capture"
            className="w-full h-full border-0"
            onLoad={() => setIsConnecting(false)}
          />
        </div>

        {/* Controls */}
        <div className="absolute bottom-0 left-0 right-0 z-10 p-6 bg-gradient-to-t from-black/70 to-transparent">
          <div className="flex items-center justify-center gap-4">
            <Button
              variant={isMuted ? "destructive" : "secondary"}
              size="icon"
              className="h-14 w-14 rounded-full"
              onClick={() => setIsMuted(!isMuted)}
            >
              {isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
            </Button>

            <Button
              variant="destructive"
              size="icon"
              className="h-16 w-16 rounded-full"
              onClick={handleEndCall}
            >
              <PhoneOff className="h-7 w-7" />
            </Button>

            <Button
              variant={isVideoOff ? "destructive" : "secondary"}
              size="icon"
              className="h-14 w-14 rounded-full"
              onClick={() => setIsVideoOff(!isVideoOff)}
            >
              {isVideoOff ? <VideoOff className="h-6 w-6" /> : <Video className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
